import { MutationCache, QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { render, RenderOptions } from "@testing-library/react";
import { BrowserRouter } from "react-router-dom";
import {
  createIDBPersister,
  currencyFormatter,
  getErrorMessage,
} from "./utils";
import { Toaster } from "@/components/ui/toaster";
import { toast } from "@/hooks/use-toast";
import { ErrorResponse } from "./types";
import { HttpResponse, http } from "msw";
import { SetupServerApi } from "msw/node";
import { I18nextProvider } from "react-i18next";
import {
  BASE_URL,
  getCurrentUserFromStorage,
  setActiveOrganizationId,
} from "./api";
import i18next from "./i18next";
import { mockLoginResponse } from "@/mocks/data";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
  mutationCache: new MutationCache({
    onError: (error: ErrorResponse | Error) => {
      if (
        (error as ErrorResponse).response?.data.message ===
        "Invalid username or password"
      ) {
        return;
      }

      toast({
        title: i18next.t("error_message.title"),
        description: getErrorMessage(error as ErrorResponse),
      });
    },
  }),
});

const persister = createIDBPersister();

const AllTheProviders = ({ children }: { children: React.ReactNode }) => {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister }}
    >
      <I18nextProvider i18n={i18next}>
        <BrowserRouter>{children}</BrowserRouter>
        <Toaster />
      </I18nextProvider>
    </PersistQueryClientProvider>
  );
};

const customRender = (
  ui: React.ReactElement,
  options?: Omit<RenderOptions, "wrapper">
) => render(ui, { wrapper: AllTheProviders, ...options });

// override render method
export { customRender as render };

export const onUnhandledRequest = "bypass";

export async function enableMocking() {
  if (import.meta.env.VITE_MSW_ENABLED === "true" && navigator.onLine) {
    const { worker } = await import("../mocks/browser");
    return worker.start({
      onUnhandledRequest,
    });
  }
}

export const errorMSWHandler = ({
  server,
  path,
  method = "post",
  status = 401,
  message = i18next.t("error_message.something_went_wrong"),
}: {
  server: SetupServerApi;
  path: string;
  method?: "post" | "get" | "put" | "delete";
  status?: number;
  message?: unknown;
}) => {
  server.use(
    http[method](BASE_URL + path, () => {
      return HttpResponse.json(
        {
          message,
        },
        {
          status,
        }
      );
    })
  );
};

export const generateErrorResponse = (
  message: string[],
  status: number = 401
): ErrorResponse => {
  return {
    response: {
      data: { errors: message, successed: false },
      status,
      statusText: "Bad Request",
      headers: {},
    },
    message: "",
    config: {},
  };
};

export const setMockUserToLocalStorage = () => {
  localStorage.setItem("access_token", mockLoginResponse.accessToken);
  localStorage.setItem("refresh_token", mockLoginResponse.refreshToken);
  localStorage.setItem("token_expiry", "1000000000000");
  setActiveOrganizationId("01968fba-e9a6-77e9-b0c0-c2e528a20d7c");
  const userInfo = getCurrentUserFromStorage();
  localStorage.setItem("user", JSON.stringify(userInfo));
};

export const matchFormattedCurrency =
  (amount: number, currencyCode: string) => (content: string) => {
    const expected = currencyFormatter(amount, currencyCode);
    const normalize = (s: string) =>
      s
        .replace(/\u00a0/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    return normalize(content) === normalize(expected);
  };
